import React from 'react';
import { MenuItem, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ExtractedFieldItem, ManualFieldDefinition } from '../../web-api-client';

export function initialFieldValue(field: ExtractedFieldItem): string {
  const type = field.dataType?.toLowerCase();
  if (type === 'number' && field.valueNumber != null) return String(field.valueNumber);
  if (type === 'date' && field.valueDate) {
    return field.valueDate instanceof Date ? field.valueDate.toISOString().slice(0, 10) : String(field.valueDate).slice(0, 10);
  }
  if (type === 'bool') {
    const value = field.rawValue?.trim().toLowerCase();
    if (value && ['true', 'yes', '1'].includes(value)) return 'true';
    if (value && ['false', 'no', '0'].includes(value)) return 'false';
    return '';
  }
  return field.rawValue ?? '';
}

export const allowedFieldValues = (definition?: ManualFieldDefinition): string[] => {
  try {
    const values = JSON.parse(definition?.allowedValuesJson ?? '[]');
    return Array.isArray(values) ? values.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
};

const TypedFieldInput: React.FC<{
  definition?: ManualFieldDefinition;
  dataType?: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ definition, dataType, value, onChange }) => {
  const { t } = useTranslation();
  const type = (definition?.dataType ?? dataType ?? 'Text').toLowerCase();
  const allowed = allowedFieldValues(definition);
  const boolean = type === 'bool';
  const select = boolean || allowed.length > 0;
  return (
    <TextField
      label={t('reviewPage.fieldValue')}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      select={select}
      type={select ? undefined : type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
      InputLabelProps={{ shrink: true }}
      inputProps={{ step: 'any', min: definition?.minValue, max: definition?.maxValue, maxLength: 4000 }}
      helperText={definition?.unit ? t('reviewPage.fieldUnit', { unit: definition.unit }) : undefined}
      fullWidth
      required
    >
      {boolean
        ? [<MenuItem key="true" value="true">{t('common.yes')}</MenuItem>,
          <MenuItem key="false" value="false">{t('common.no')}</MenuItem>]
        : allowed.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
    </TextField>
  );
};

export default TypedFieldInput;
